import React from "react";
import {
    Paper,
    Grid,
    ListItem,
    IconButton,
    ListItemAvatar,
    ListItemText,
    Stack,
    Divider,
    Box,
    Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import moment from "moment";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import CheckIcon from "@mui/icons-material/Check";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import Alert from "@mui/material/Alert";
import Tooltip from "@mui/material/Tooltip";
import SpeakerNotesTwoToneIcon from "@mui/icons-material/SpeakerNotesTwoTone";

function DeleteExpenseModal(props) {
    const { deleteExpense, expense, openDelete, setOpenDelete } = props;

    const handleClose = () => {
        setOpenDelete(false);
    };

    const handleDelete = () => {
        deleteExpense(expense.id);
        setOpenDelete(false);
    };

    return (
        <div>
            <Dialog
                open={openDelete}
                onClose={handleClose}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">
                    <Alert severity="error">
                        Permanently delete this expense?
                    </Alert>
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        {moment(expense.date_purchased).format(
                            "ddd, MMMM Do YYYY"
                        )}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" onClick={handleClose}>
                        Close
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleDelete}
                        autoFocus
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}

function ImageDialog(props) {
    const { openImage, setOpenImage, expense } = props;
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down("md"));

    const handleClose = () => {
        setOpenImage(false);
    };

    return (
        <div>
            <Dialog
                fullScreen={fullScreen}
                open={openImage}
                onClose={handleClose}
                aria-labelledby="responsive-dialog-title"
            >
                <DialogTitle id="responsive-dialog-title">
                    Expense id: {expense.id}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        <img
                            src={expense.receipt_pic}
                            alt="receipt"
                            style={{ width: "100%" }}
                        />
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleClose}
                        autoFocus
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}

export default function ExpenseCard(props) {
    const { user, isAdmin } = props;
    const { expense } = props;
    const {
        approveExpense,
        deleteExpense,
        setEditExpense,
        setEditing,
        setOpen,
        darkState,
    } = props;
    const [openImage, setOpenImage] = React.useState(false);
    const [openDelete, setOpenDelete] = React.useState(false);
    const [openProcessedInfo, setOpenProcessedInfo] = React.useState(false);
    const cardIsEditable = !expense.is_approved;

    const handleClickOpen = () => {
        setOpenImage(true);
    };

    const handleEdit = () => {
        if (!cardIsEditable) {
            return;
        }
        setEditExpense(expense);
        setEditing(true);
        setOpen(true);
    };

    const handleDelete = (event) => {
        if (event && typeof event.stopPropagation === "function") {
            event.stopPropagation();
        }
        setOpenDelete(true);
    };

    const handleShowProcessedInfo = () => {
        setOpenProcessedInfo(true);
    };

    const handleCloseProcessedInfo = () => {
        setOpenProcessedInfo(false);
    };

    const handleCardClick = (event) => {
        // Check if click is coming from specific interactive buttons we want to ignore
        const clickedElement = event.target;
        const isReceiptButton =
            clickedElement.closest("button") &&
            clickedElement.closest("button").textContent.includes("$");
        const isApproveButton =
            clickedElement.closest("button") &&
            (clickedElement.closest("button").textContent.includes("Approve") ||
                clickedElement
                    .closest("button")
                    .textContent.includes("Approved"));

        if (isReceiptButton || isApproveButton) {
            return; // Don't handle card click if it's from these specific buttons
        }

        if (cardIsEditable) {
            handleEdit();
        } else {
            handleShowProcessedInfo();
        }
    };

    const handleCardKeyDown = (event) => {
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            // For keyboard navigation, we don't need to worry about button conflicts
            // as much since focus should be managed properly
            if (cardIsEditable) {
                handleEdit();
            } else {
                handleShowProcessedInfo();
            }
        }
    };

    return (
        <>
            <Paper
                variant="outlined"
                sx={{
                    my: 2,
                    width: "100%",
                    maxWidth: "500px",
                    border: darkState ? "1.5px solid" : "3px solid",
                    borderColor: "primary.main",
                    borderRadius: "16px",
                    position: "relative",
                    cursor: "pointer",
                    transition: "transform 0.2s ease-in-out",
                    "&:hover": {
                        transform: "translateY(-4px)",
                    },
                }}
                key={expense.id}
            >
                {!expense.is_approved ? (
                    <>
                        <IconButton
                            aria-label="delete expense"
                            onClick={handleDelete}
                            size="medium"
                            sx={{
                                position: "absolute",
                                top: 8,
                                right: 8,
                                zIndex: 2,
                            }}
                        >
                            <CloseIcon fontSize="medium" />
                        </IconButton>
                    </>
                ) : null}
                <Grid container wrap="nowrap" spacing={2}>
                    <Grid item xs zeroMinWidth>
                        <ListItem
                            sx={{
                                alignItems: "stretch",
                            }}
                        >
                            <ListItemAvatar
                                sx={{
                                    marginRight: "1rem",
                                    marginBottom: "1rem",
                                }}
                            >
                                <Button
                                    sx={{
                                        marginRight: "0.5rem",
                                        marginLeft: "0.5rem",
                                        marginTop: "1.5rem",
                                        marginBottom: "1.5rem",
                                    }}
                                    variant="contained"
                                    size="small"
                                    color={`${
                                        expense.is_approved
                                            ? "success"
                                            : "primary"
                                    }`}
                                    startIcon={
                                        expense.is_approved ? (
                                            <CheckIcon />
                                        ) : (
                                            <ReceiptLongOutlinedIcon />
                                        )
                                    }
                                    onClick={(event) => {
                                        event.preventDefault();
                                        event.stopPropagation();
                                        handleClickOpen();
                                    }}
                                >
                                    {`$${parseFloat(expense.price).toFixed(2)}`}
                                </Button>
                                <Divider />
                                <ListItemText
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        marginTop: "1rem",
                                    }}
                                    primary={
                                        isAdmin ? (
                                            expense.is_approved ? (
                                                <Button
                                                    variant="outlined"
                                                    startIcon={<CheckBoxIcon />}
                                                    color="success"
                                                    size="small"
                                                    onClick={(event) => {
                                                        event.stopPropagation();
                                                        approveExpense(
                                                            expense.id
                                                        );
                                                    }}
                                                >
                                                    Approved
                                                </Button>
                                            ) : (
                                                <Button
                                                    variant="outlined"
                                                    color="inherit"
                                                    size="small"
                                                    onClick={(event) => {
                                                        event.stopPropagation();
                                                        approveExpense(
                                                            expense.id
                                                        );
                                                    }}
                                                >
                                                    Approve
                                                </Button>
                                            )
                                        ) : (
                                            <span>
                                                {expense.is_approved
                                                    ? "Approved"
                                                    : "Pending"}
                                            </span>
                                        )
                                    }
                                />
                            </ListItemAvatar>
                            <ListItemText
                                onClick={handleCardClick}
                                onKeyDown={handleCardKeyDown}
                                role="button"
                                tabIndex={0}
                                sx={{
                                    cursor: cardIsEditable
                                        ? "pointer"
                                        : "default",
                                }}
                                primary={
                                    <div
                                        style={{
                                            fontWeight: "700",
                                            marginBottom: ".25rem",
                                        }}
                                    >
                                        {moment(expense.date_purchased).format(
                                            "ddd, MMM Do YYYY"
                                        )}
                                    </div>
                                }
                                secondary={
                                    <>
                                        {isAdmin
                                            ? `${expense.user.first_name} ${expense.user.last_name}`
                                            : ""}
                                        {isAdmin ? <br /> : ""}
                                        {expense.quote
                                            ? `Quote: ${expense.quote.number}`
                                            : ""}
                                        {expense.project
                                            ? `Project: ${expense.project.number}`
                                            : ""}
                                        {expense.service
                                            ? `Service: ${expense.service.number}`
                                            : ""}
                                        {expense.hse
                                            ? `HSE: ${expense.hse.number}`
                                            : ""}
                                        <br />
                                        Merchant: {expense.merchant}
                                    </>
                                }
                            />
                        </ListItem>
                        <ImageDialog
                            openImage={openImage}
                            setOpenImage={setOpenImage}
                            expense={expense}
                        />
                        <DeleteExpenseModal
                            expense={expense}
                            openDelete={openDelete}
                            setOpenDelete={setOpenDelete}
                            deleteExpense={deleteExpense}
                        />
                    </Grid>
                </Grid>
                {expense.notes ? (
                    <Tooltip title={expense.notes} enterTouchDelay={0}>
                        <IconButton
                            size="small"
                            aria-label="notes"
                            sx={{
                                position: "absolute",
                                right: 12,
                                bottom: 12,
                            }}
                            onClick={(event) => event.stopPropagation()}
                        >
                            <SpeakerNotesTwoToneIcon />
                        </IconButton>
                    </Tooltip>
                ) : null}
            </Paper>
            <Dialog
                open={openProcessedInfo}
                onClose={handleCloseProcessedInfo}
                aria-labelledby="processed-dialog-title"
            >
                <DialogTitle id="processed-dialog-title">
                    Already Processed
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        This expense has already been processed. Contact your
                        administrator if you need to request any changes.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseProcessedInfo} autoFocus>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
